import React, {useMemo} from "react";
import InputForm from "../../../../components/input/input-form.jsx";
import platePatternUtils from "../../../../utils/plate-pattern-utils.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import {
    getBrandModels,
    getVehicleColors,
    getVehicleDataByPlate,
    getVehicleType
} from "../../../../utils/vehicle-utils.js";
import {getCities} from "../../../../utils/location-utils.js";
import Dropzone from "../../../../components/dropzone/dropzone.jsx";

const getLegacyAttachId = (name = "") => {
    const parts = String(name).split(".");
    if (parts.length <= 1) return name;
    parts.pop();
    return parts.join(".");
};

function EncounterVehiclesForm({ data, formKey, individuals, allVehicles ,onChange }){
    const updateField = (field, value) => {
        onChange?.({
            ...(data ?? {}),
            [field]: value,
        });
    };

    const updateFields = (fields) => {
        onChange?.({
            ...(data ?? {}),
            ...fields,
        });
    };

    const individualOptions = useMemo(() =>
            (individuals ?? []).map((individual, index) => ({
                value: individual?.id ?? individual?._tempKey ?? individual?.cpf ?? index,
                label: individual?.name
                    ? individual.name
                    : individual?.cpf
                        ? projectUtils.formatCpfToView(individual.cpf)
                        : `Indivíduo ${index + 1}`,
            })),
        [individuals]);

    const currentVehicleKey = data?.id ?? data?._tempKey ?? data?.plate ?? formKey;

    const otherVehicles = useMemo(() =>
            (allVehicles ?? []).filter((vehicle, index) => {
                const vehicleKey = vehicle?.id ?? vehicle?._tempKey ?? vehicle?.plate ?? index;
                return vehicleKey !== currentVehicleKey;
            }),
        [allVehicles, currentVehicleKey]);

    const usedInOtherVehicles = useMemo(() => {
        const used = new Set();

        otherVehicles.forEach((vehicle) => {
            const conductorValue = vehicle?.conductor?.value;
            if (conductorValue != null) {
                used.add(conductorValue);
            }

            (vehicle?.passengers ?? []).forEach((passenger) => {
                if (passenger?.value != null) {
                    used.add(passenger.value);
                }
            });
        });

        return used;
    }, [otherVehicles]);


    const passengerIds = useMemo(
        () => (data?.passengers ?? []).map((item) => item?.value),
        [data?.passengers]
    );

    const conductorId = data?.conductor?.value ?? null;

    const conductorOptions = useMemo(() =>
            individualOptions.filter((option) => {
                const isPassengerInCurrentVehicle = passengerIds.includes(option.value);
                const isUsedInOtherVehicle = usedInOtherVehicles.has(option.value);

                return !isPassengerInCurrentVehicle && !isUsedInOtherVehicle;
            }),
        [individualOptions, passengerIds, usedInOtherVehicles]);

    const passengerOptions = useMemo(() =>
            individualOptions.filter((option) => {
                const isConductorInCurrentVehicle = option.value === conductorId;
                const isUsedInOtherVehicle = usedInOtherVehicles.has(option.value);

                return !isConductorInCurrentVehicle && !isUsedInOtherVehicle;
            }),
        [individualOptions, conductorId, usedInOtherVehicles]);

    const handlePlateChange = async (value) => {
        updateField("plate", value);

        const normalizedPlate = String(value ?? "")
            .replace(/[^a-zA-Z0-9]/g, "")
            .toUpperCase();

        if (normalizedPlate.length !== 7) return;

        try {
            const vehicleData = await getVehicleDataByPlate(normalizedPlate);
            console.log(vehicleData);

            if (!vehicleData) return;

            const city = typeof vehicleData?.city === "string"
                ? vehicleData.city
                    ? {value: vehicleData.city, label: vehicleData.city}
                    : data?.city ?? null
                : vehicleData?.city ?? data?.city ?? null;

            updateFields({
                plate: vehicleData?.plate ?? normalizedPlate,
                city,
                type: vehicleData?.type ?? data?.type ?? null,
                color: vehicleData?.color ?? data?.color ?? null,
                chassis: vehicleData?.chassis ?? data?.chassis ?? "",
                renavam: vehicleData?.renavam ?? data?.renavam ?? "",
                modelYear: vehicleData?.modelYear ?? data?.modelYear ?? "",
                fabricationYear: vehicleData?.fabricationYear ?? data?.fabricationYear ?? "",
                nameOwner: vehicleData?.nameOwner ?? data?.nameOwner ?? "",
                brandModel: vehicleData?.brandModel ?? data?.brandModel ?? null,
                engineNumber: vehicleData?.engineNumber ?? data?.engineNumber ?? "",
            });
        } catch (error) {
            console.error("Erro ao buscar placa:", error);
        }
    };

    const handleSetAttaches = (next) => {
        const currentAttaches = Array.isArray(data?.attaches) ? data.attaches : [];
        const resolvedAttaches =
            typeof next === "function" ? next(currentAttaches) : next;

        const normalizedAttaches = (Array.isArray(resolvedAttaches) ? resolvedAttaches : []).map((attach) => {
            if (attach instanceof File && attach?.new) {
                attach.attachId = getLegacyAttachId(attach.name);
            }

            return attach;
        });

        updateField("attaches", normalizedAttaches);
    };


    return (
        <div className="border border-zinc-200 rounded-md p-3 space-y-3">
            <div className="flex gap-4">
                <InputForm
                    id={`plate-${formKey}`}
                    label="Placa *"
                    classNameWrapper="flex-1"
                    value={platePatternUtils.formatPlate(data?.plate ?? "")}
                    onChange={(e) => handlePlateChange(e.target.value)}
                />
                <SelectComponent
                    id={`brandModel-${formKey}`}
                    label="Marca/Modelo"
                    classNameWrapper="flex-1"
                    isAsync={true}
                    loadOptions={getBrandModels}
                    defaultOptions={[]}
                    value={data?.brandModel ?? null}
                    onChange={(val) => updateField("brandModel", val ?? null)}
                />
                <SelectComponent
                    id={`type-${formKey}`}
                    label="Tipo (Denatram)"
                    classNameWrapper="flex-1"
                    isAsync={true}
                    loadOptions={getVehicleType}
                    defaultOptions={true}
                    value={data?.type ?? null}
                    onChange={(val) => updateField("type", val ?? null)}
                />
                <SelectComponent
                    id={`color-${formKey}`}
                    label="Cor"
                    classNameWrapper="flex-1"
                    isAsync={true}
                    loadOptions={getVehicleColors}
                    defaultOptions={true}
                    value={data?.color ?? null}
                    onChange={(val) => updateField("color", val ?? null)}
                />
            </div>
            <div className="flex gap-4">
                <InputForm
                    id={`fabricationYear-${formKey}`}
                    label="Ano de fabricação"
                    classNameWrapper="flex-1"
                    value={data?.fabricationYear ?? ""}
                    onChange={(e) => updateField("fabricationYear", e.target.value)}
                />
                <InputForm
                    id={`modelYear-${formKey}`}
                    label="Ano do modelo"
                    classNameWrapper="flex-1"
                    value={data?.modelYear ?? ""}
                    onChange={(e) => updateField("modelYear", e.target.value)}
                />
                <InputForm
                    id={`renavam-${formKey}`}
                    label="Renavam"
                    classNameWrapper="flex-1"
                    value={data?.renavam ?? ""}
                    onChange={(e) => updateField("renavam", e.target.value)}
                />
                <InputForm
                    id={`chassis-${formKey}`}
                    label="Chassi"
                    classNameWrapper="flex-1"
                    value={data?.chassis ?? ""}
                    onChange={(e) => updateField("chassis", e.target.value)}
                />
                <InputForm
                    id={`engineNumber-${formKey}`}
                    label="Motor"
                    classNameWrapper="flex-1"
                    value={data?.engineNumber ?? ""}
                    onChange={(e) => updateField("engineNumber", e.target.value)}
                />
                <SelectComponent
                    id={`city-${formKey}`}
                    label="Município"
                    classNameWrapper="flex-1"
                    isAsync={true}
                    loadOptions={getCities}
                    defaultOptions={[]}
                    value={data?.city ?? null}
                    onChange={(val) => updateField("city", val ?? null)}
                />
            </div>
            <div className="flex gap-4">
                <InputForm
                    id={`nameOwner-${formKey}`}
                    label="Nome do proprietário"
                    classNameWrapper="flex-1"
                    value={data?.nameOwner ?? ""}
                    onChange={(e) => updateField("nameOwner", e.target.value)}
                />
            </div>
            <div className="flex gap-4">
                <SelectComponent
                    id={`conductor-${formKey}`}
                    label="Condutor"
                    classNameWrapper="flex-1"
                    options={conductorOptions}
                    value={data?.conductor ?? null}
                    onChange={(val) =>
                        updateFields({
                            conductor: val ?? null,
                            passengers: (data?.passengers ?? []).filter(
                                (passenger) => passenger?.value !== val?.value
                            ),
                        })
                    }
                />
                <SelectComponent
                    id={`passengers-${formKey}`}
                    label="Passageiros"
                    classNameWrapper="flex-1"
                    isMulti={true}
                    options={passengerOptions}
                    value={data?.passengers ?? []}
                    onChange={(val) => {
                        const nextPassengers = val ?? [];
                        const hasSelectedConductor = nextPassengers.some(
                            (passenger) => passenger?.value === data?.conductor?.value
                        );

                        updateFields({
                            passengers: nextPassengers,
                            conductor: hasSelectedConductor ? null : data?.conductor ?? null,
                        });
                    }}
                />
            </div>
            <div className="flex flex-col space-y-3">
                <div className="flex">
                    <p className="font-bold text-sm pl-1 text-gray-500">Anexos</p>
                </div>
                <div>
                    <div className="w-full">
                        <Dropzone
                            title="Anexos"
                            attaches={data?.attaches ?? []}
                            setAttaches={handleSetAttaches}
                            withoutCard={true}
                        />
                    </div>
                </div>
            </div>

        </div>
    )
}

export default React.memo(EncounterVehiclesForm);
